import { Injectable, ApplicationRef, Inject, createComponent, ChangeDetectorRef, DOCUMENT } from "@angular/core";
import { Store } from "@ngrx/store";

import { ContextToastAction, ContextToastComponent, ContextToastType } from "../../components/context-toast/context-toast.component";

import { dialogHostId } from "../../utils/dialog-host/dialog-host";
import type { DialogMessage } from "../dialog/dialog.interface";
import { ShopifyAppBridgeService } from "../shopify-app-bridge/shopify-app-bridge.service";
import type { RootState } from "../../store";
import * as appActions from "../../store/app/actions";

// Type for toast position
export type ToastPosition = "top-left" | "top-right" | "top-center" | "bottom-left" | "bottom-right" | "bottom-center";
export type ToastType = ContextToastType;
export type ToastAction = ContextToastAction;

const EXIT_ANIMATION_MS = 200;
// Polaris Toast durations: 5s, 10s with an action so there is time to use it
const DURATION_MS = 5000;
const DURATION_WITH_ACTION_MS = 10000;

const plainText = (text: string | null | undefined): string => (text ?? "").replace(/<[^>]*>/g, "").trim();

// Every notification goes through here, following Shopify's alerts rules (App design guidelines > Alerts, Built for
// Shopify 4.2.4):
// - success and info are toasts with one short text (the title; the message is not shown): App Bridge's inside
//   Shopify, our copy of Polaris Toast at the bottom centre outside;
// - errors are never toasts: they go to the critical banner at the top of the page (app-page-error-banner, with the
//   message as its reason), which stays until dismissed. Only a connection error (connection: true) is a toast,
//   critical and kept until closed, and so is an error on a screen with no page banner (login, onboarding, modals).
// position, showAsAbsolute, anchor and the selector are no longer used: every toast shows at the bottom centre.
@Injectable({
  providedIn: "root",
  deps: [ChangeDetectorRef],
})
export class ToastService {
  private stack: HTMLElement | null = null;

  constructor(
    private readonly appRef: ApplicationRef,
    @Inject(DOCUMENT) private readonly document: Document,
    private readonly shopifyAppBridgeService: ShopifyAppBridgeService,
    private readonly store: Store<RootState>,
  ) {}

  public show(
    {
      title,
      message,
      type = "success",
      duration,
      action,
      connection = false,
    }: {
      title: string;
      message: string;
      type?: ToastType;
      duration?: number;
      action?: ToastAction;
      showAsAbsolute?: boolean;
      position?: ToastPosition;
      anchor?: "element" | "viewport";
      /** A connection error: the one error shown as a toast (critical, until closed). */
      connection?: boolean;
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    uniqueCSSselector?: string,
  ): void {
    const dialogId = dialogHostId();

    if (type === "error") {
      if (!connection && !dialogId && this.document.querySelector("app-page-error-banner")) {
        this.store.dispatch(appActions.showPageError({ pageError: { title: plainText(title), message: plainText(message) } }));
      } else {
        this.showLocal([title, message].map(plainText).filter(Boolean).join(": "), "error", null, null);
      }
      return;
    }

    const content = plainText(title) || plainText(message);
    const visibleFor = duration ?? (action ? DURATION_WITH_ACTION_MS : DURATION_MS);

    // Inside a Shopify modal App Bridge has no toast API: the page that opened it shows the notification
    if (dialogId && !action) {
      const toast: DialogMessage = { type: "dialog-toast", dialogId, title, message, duration };
      window.opener.postMessage(toast, window.location.origin);
      return;
    }
    if (!dialogId && this.shopifyAppBridgeService.isEmbedded) {
      window.shopify.toast.show(content, {
        duration: visibleFor,
        ...(action ? { action: action.label, onAction: action.callback } : {}),
      });
      return;
    }

    this.showLocal(content, type, visibleFor, action ?? null);
  }

  private showLocal(content: string, type: ToastType, duration: number | null, action: ToastAction | null): void {
    const stack = this.getStack();
    const containerElement = this.document.createElement("div");
    stack.append(containerElement);

    const toastComponentRef = createComponent(ContextToastComponent, {
      hostElement: containerElement,
      environmentInjector: this.appRef.injector,
    });
    toastComponentRef.instance.title = content;
    toastComponentRef.instance.type = type;
    toastComponentRef.instance.duration = duration;
    toastComponentRef.instance.action = action;
    this.appRef.attachView(toastComponentRef.hostView);

    let removed = false;
    const remove = (): void => {
      if (removed) return;
      removed = true;
      this.appRef.detachView(toastComponentRef.hostView);
      toastComponentRef.destroy();
      containerElement.remove();
      if (!stack.childElementCount) {
        stack.remove();
        this.stack = null;
      }
    };

    toastComponentRef.instance.dismiss.subscribe(() => {
      toastComponentRef.instance.animation = "out";
      toastComponentRef.changeDetectorRef.detectChanges();
      setTimeout(remove, EXIT_ANIMATION_MS);
    });
  }

  // Polaris ToastManager: one column at the bottom centre, 20px up, the newest toast last
  private getStack(): HTMLElement {
    if (this.stack) return this.stack;

    const stack = this.document.createElement("div");
    stack.className = "toast-stack";
    Object.assign(stack.style, {
      position: "fixed",
      zIndex: "9999",
      right: "0",
      bottom: "20px",
      left: "0",
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      gap: "8px",
      pointerEvents: "none",
    });
    this.document.body.append(stack);
    this.stack = stack;
    return stack;
  }
}
